import { useEffect, useState, type ReactNode } from 'react';

import { useMyExperimentAssignments } from '@/lib/api/hooks';
import { usePostPurchaseOffer } from '@/lib/api/upsellHooks';
import {
  PostCheckoutSetupScreen,
  clearPostCheckoutSetup,
  postCheckoutSetupActive,
} from './postCheckoutSetup';

// A stack's add-ons are still being set up for a few seconds after the
// account is claimed. Hold the setup screen until they are, so the
// post-purchase popup is ready when the welcome screen appears, but never
// longer than this.
const STACK_SETUP_WAIT_MS = 15_000;

/**
 * Wraps the whole welcome step (the welcome screen AND the post-purchase
 * popup mount, see OnboardingShell) so the popup can't open over the loader.
 * Straight from checkout, keeps the claim screen's "Setting up your account"
 * state up until the popup has everything it needs: the purchase offer (and,
 * for a stack, every add-on set up) plus the experiment assignments it gates
 * on. The welcome screen then opens with the popup ready instead of flashing
 * up and handing back to a loader. Any other visit only waits on a stack
 * that is still being set up.
 */
export function StackSetupGate({ children }: { children: ReactNode }) {
  const [fromCheckout] = useState(postCheckoutSetupActive);
  const offer = usePostPurchaseOffer();
  const assignments = useMyExperimentAssignments();
  const [waitExpired, setWaitExpired] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaitExpired(true), STACK_SETUP_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  const stackPending = Boolean(offer.data?.plan_upsell_pending);
  // Straight from checkout the order can trail the claim by a few seconds,
  // so an answer without a purchase isn't final (the offer keeps polling):
  // keep waiting for it until the deadline releases the visit.
  const popupLoading =
    fromCheckout &&
    (offer.isPending || assignments.isPending || offer.data?.purchase == null);
  const waiting = !waitExpired && (stackPending || popupLoading);
  useEffect(() => {
    if (fromCheckout && !waiting) clearPostCheckoutSetup();
  }, [fromCheckout, waiting]);

  if (waiting) return <PostCheckoutSetupScreen />;
  return <>{children}</>;
}
